import { useState } from "react";
import { createRoute, Link, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Price, SectionTitle, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { useIdentities } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopWithdraw } from "./desktop/withdraw.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  提现
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这一页只做一件事：把账上的钱申请出来。**
 *    以前它是商家端首页最下面的一小块，跟加价率、邀请链接、等级挤在一起 ——
 *    动钱的操作藏在一屏杂物里，既难找又容易点错。
 *
 * 🔴 **申请前就要告诉他到手多少。**
 *    平台向自然人支付劳务报酬有法定代扣代缴义务，但"不解释就扣"是产品事故：
 *    他申请 ¥1000 收到 ¥840，只会以为平台吞了钱。
 */

export interface Balance {
  withdrawable: number;
  frozen: number;
}
export interface Quote {
  amount: number;
  taxAmount: number;
  taxSummary: string;
  actualEstimate: number;
}
export interface WithdrawalRow {
  id: number;
  withdrawalNo: string;
  amount: number;
  /** 实际到账 = amount − fee − taxAmount。⚠️ 是快照，不重算。 */
  actualAmount: number;
  taxAmount: number;
  status: string;
  createdAt: string;
  rejectReason: string;
  failReason: string;
}

export const STATUS_LABEL: Record<string, string> = {
  pending: "待审核",
  approved: "已通过，待打款",
  paid: "已打款",
  rejected: "已驳回",
};

function Withdraw() {
  const search = useSearch({ from: "/withdraw" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();

  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const balance = useQuery({
    queryKey: ["merchant", orgId, "balance"],
    queryFn: () => orgFetch<Balance>("/balance", orgId!),
    enabled: Boolean(orgId),
  });
  /** ⚠️ 接口直接回数组，不是 `{items}` —— 包一层会拿到 undefined。 */
  const history = useQuery({
    queryKey: ["merchant", orgId, "withdrawals"],
    queryFn: () => orgFetch<WithdrawalRow[]>("/withdrawals", orgId!),
    enabled: Boolean(orgId),
  });

  const [amount, setAmount] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [payeeAccount, setPayeeAccount] = useState("");

  /** ⚠️ 输入的是元，接口要分 —— 换算只做一次，别在几处各转一遍。 */
  const cents = Math.round(Number(amount || 0) * 100);
  const withdrawable = balance.data?.withdrawable ?? 0;
  /**
   * ⚠️ 超过可提现余额就别去试算了。
   *    服务端当然会拒，但试算会先回来一个按天文数字算出的税额，
   *    满屏 ¥1991046795054724.80 比一句"余额不足"吓人得多。
   */
  const tooMuch = cents > withdrawable;

  /**
   * 试算代扣。⚠️ 金额变了就重算 —— 劳务报酬是**超额累进**，
   *    金额跨档时税率会跳，按固定比例心算是对不上的。
   */
  const quote = useQuery({
    queryKey: ["withdraw-quote", orgId, cents],
    queryFn: () => orgFetch<Quote>(`/withdrawals/quote?amount=${cents}`, orgId!),
    enabled: Boolean(orgId) && cents > 0 && !tooMuch,
  });

  const submit = useMutation({
    mutationFn: () =>
      orgFetch("/withdrawals", orgId!, {
        method: "POST",
        body: JSON.stringify({
          amount: cents,
          payee: { kind: "bank", name: payeeName, account: payeeAccount },
        }),
      }),
    onSuccess: () => {
      setAmount("");
      void qc.invalidateQueries({ queryKey: ["merchant", orgId] });
    },
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。**
   *    下面那个查询在 `orgId` 为空时是 disabled 的，而 disabled 的查询
   *    `isPending` 恒为 true —— 未登录、或登录了但名下没有店的人，
   *    看到的是一个**永远停不下来的圈**，页面上没有任何提示也没有出路。
   */
  if (!isPending && !orgId) {
    return (
      <SubPage title="提现">
        <Empty title="你还不是商家" hint="提现是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} />
      </SubPage>
    );
  }
  if (isPending || balance.isPending) return <SubPage title="提现"><Spinner /></SubPage>;
  if (balance.error) return <SubPage title="提现"><ErrorBlock error={balance.error} /></SubPage>;

  return (
    <SubPage title="提现">
      <div className="space-y-3 px-3 py-3 pb-8">
        {/* ── 能提多少 ── */}
        <Card className="px-4 py-5 text-center">
          <p className="text-[13px] text-muted-foreground">可提现</p>
          <p className="mt-1 text-3xl">
            <Price cents={withdrawable} className="text-brand-600" />
          </p>
          {(balance.data?.frozen ?? 0) > 0 && (
            <p className="mt-2 text-[12px] text-muted-foreground">
              另有 <Price cents={balance.data!.frozen} /> 提现中
            </p>
          )}
          {/* ⚠️ 光有数字不够 —— 他要能查到每一笔从哪来、为什么被扣 */}
          <Link
            to="/earnings"
            search={{ orgId } as never}
            className="mt-2 inline-block text-[13px] text-brand-600"
          >
            查看每一笔明细 →
          </Link>
        </Card>

        {withdrawable <= 0 ? (
          <Card className="px-4 py-4 text-[13px] leading-relaxed text-muted-foreground">
            账上还没有可提现的钱。
            {/*
              ⚠️ 说清楚待结算为什么不能提 —— 不解释的话商家会以为系统扣了他的钱。
                 结算时点压到"确认收货 + 15 天"是为了覆盖售后维权期：
                 建材退货率不低，收货就把钱结出去的话用户退货时亏的是平台。
            */}
            <br />
            待结算的钱在售后期内（确认收货 + 15 天）不能提现。
          </Card>
        ) : (
          <>
            <SectionTitle>申请提现</SectionTitle>
            <Card className="space-y-3 px-4 py-4">
              <Input
                label="提现金额（元）"
                inputMode="decimal"
                placeholder={`最多 ${(withdrawable / 100).toFixed(2)}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {tooMuch && (
                <p className="text-xs text-destructive">
                  超过可提现余额（<Price cents={withdrawable} />）
                </p>
              )}

              {/*
                🔴 **代扣要在申请前说清楚。**
                   平台向自然人支付劳务报酬有法定代扣代缴义务 ——
                   但他申请 ¥1000 收到 ¥840 却没人告诉过他，那是产品事故。
                ⚠️ 企业不代扣（他们开票自行申报），所以税额为 0 时这一块不出现。
              */}
              {!tooMuch && quote.data && quote.data.taxAmount > 0 && (
                <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800">
                  <p>
                    需代扣个税 <Price cents={quote.data.taxAmount} />，预计到手{" "}
                    <Price cents={quote.data.actualEstimate} />
                  </p>
                  <p className="mt-0.5 text-amber-700">{quote.data.taxSummary}</p>
                  <p className="mt-0.5">平台按规定替你预扣预缴，年度汇算时多退少补。</p>
                </div>
              )}

              <Input
                label="收款人姓名"
                value={payeeName}
                onChange={(e) => setPayeeName(e.target.value)}
              />
              <Input
                label="银行卡号"
                inputMode="numeric"
                value={payeeAccount}
                onChange={(e) => setPayeeAccount(e.target.value)}
              />

              {submit.error && <ErrorBlock error={submit.error} />}
              {submit.isSuccess && (
                <p className="text-[13px] text-emerald-700">已提交，等待平台审核打款。</p>
              )}
              <Button
                className="w-full"
                loading={submit.isPending}
                disabled={!cents || tooMuch || !payeeName.trim() || !payeeAccount.trim()}
                onClick={() => submit.mutate()}
              >
                提交申请
              </Button>
            </Card>
          </>
        )}

        {/* ── 提现记录 ── */}
        {(history.data?.length ?? 0) > 0 && (
          <>
            <SectionTitle>提现记录</SectionTitle>
            <Card className="overflow-hidden">
              {history.data!.map((w, i) => (
                <div
                  key={w.id}
                  className={`px-4 py-3 ${i === history.data!.length - 1 ? "" : "border-b border-border"}`}
                >
                  <div className="flex items-center justify-between text-[14px]">
                    <Price cents={w.amount} />
                    <span className="text-[13px] text-muted-foreground">
                      {STATUS_LABEL[w.status] ?? w.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {new Date(w.createdAt).toLocaleString("zh-CN", { hour12: false })}
                    {/* ⚠️ 扣了税就要说到手多少 —— 不说的话他会以为平台吞了钱 */}
                    {w.taxAmount > 0 && (
                      <>
                        {" · 代扣个税 "}
                        <Price cents={w.taxAmount} />
                        {" · 到手 "}
                        <Price cents={w.actualAmount} />
                      </>
                    )}
                  </p>
                  {/* ⚠️ 驳回/失败原因必须显示 —— 只说"已驳回"的话他不知道该怎么改 */}
                  {(w.rejectReason || w.failReason) && (
                    <p className="mt-1 text-[12px] text-destructive">
                      原因：{w.rejectReason || w.failReason}
                    </p>
                  )}
                </div>
              ))}
            </Card>
          </>
        )}
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function WithdrawRoute() {
  return useIsDesktop() ? <DesktopWithdraw /> : <Withdraw />;
}

export const withdrawRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/withdraw",
  component: WithdrawRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
